/**
 * Unified type exports
 * Import shared types from this single entry point.
 */

export type * from "../drizzle/schema";
export * from "./_core/errors";

export const TRANSACTION_CATEGORIES = [
  "salary",
  "freelance",
  "investment",
  "refund",
  "transfer_in",
  "food",
  "groceries",
  "bills",
  "telecom",
  "transport",
  "entertainment",
  "shopping",
  "healthcare",
  "education",
  "rent",
  "insurance",
  "transfer_out",
  "withdrawal",
  "credit_card_payment",
  "loan_payment",
  "atm_withdrawal",
  "fees_charges",
  "other",
  "uncategorized",
] as const;

export type TransactionCategory = (typeof TRANSACTION_CATEGORIES)[number];

export const CASH_IN_CATEGORIES: TransactionCategory[] = [
  "salary",
  "freelance",
  "investment",
  "refund",
  "transfer_in",
];

export const CASH_OUT_CATEGORIES: TransactionCategory[] = [
  "food",
  "groceries",
  "bills",
  "telecom",
  "transport",
  "entertainment",
  "shopping",
  "healthcare",
  "education",
  "rent",
  "insurance",
  "transfer_out",
  "withdrawal",
  "credit_card_payment",
  "loan_payment",
  "atm_withdrawal",
  "fees_charges",
  "other",
  "uncategorized",
];

export const CATEGORY_LABELS: Record<TransactionCategory, string> = {
  salary: "Salary",
  freelance: "Freelance",
  investment: "Investment",
  refund: "Refund",
  transfer_in: "Transfer In",
  food: "Food & Dining",
  groceries: "Groceries",
  bills: "Bills & Utilities",
  telecom: "Telecom",
  transport: "Transport",
  entertainment: "Entertainment",
  shopping: "Shopping",
  healthcare: "Healthcare",
  education: "Education",
  rent: "Rent",
  insurance: "Insurance",
  transfer_out: "Transfer Out",
  withdrawal: "Withdrawal",
  credit_card_payment: "Credit Card Payment",
  loan_payment: "Loan Payment",
  atm_withdrawal: "ATM Withdrawal",
  fees_charges: "Fees & Charges",
  other: "Other",
  uncategorized: "Uncategorized",
};

export const CATEGORY_ICONS: Record<TransactionCategory, string> = {
  salary: "Banknote",
  freelance: "Briefcase",
  investment: "TrendingUp",
  refund: "RotateCcw",
  transfer_in: "ArrowDownLeft",
  food: "Utensils",
  groceries: "ShoppingCart",
  bills: "FileText",
  telecom: "Smartphone",
  transport: "Car",
  entertainment: "Film",
  shopping: "ShoppingBag",
  healthcare: "Heart",
  education: "GraduationCap",
  rent: "Home",
  insurance: "Shield",
  transfer_out: "ArrowUpRight",
  withdrawal: "Wallet",
  credit_card_payment: "CreditCard",
  loan_payment: "Landmark",
  atm_withdrawal: "Banknote",
  fees_charges: "Receipt",
  other: "MoreHorizontal",
  uncategorized: "HelpCircle",
};

export interface ParsedTransaction {
  type: "cash_in" | "cash_out";
  amount: number;
  merchant: string;
  category: TransactionCategory;
  transactionDate: number;
  balance?: number;
  currency: string;
  amountInKwd?: number;
  fxRateUsed?: number;
  sourceMessage: string;
  accountNumber?: string;
  isInterAccount?: boolean;
}

// Approximate exchange rates to KWD (Kuwaiti Dinar)
// KWD is one of the highest-valued currencies
export const CURRENCY_TO_KWD: Record<string, number> = {
  KWD: 1,
  KD: 1,
  USD: 0.307,
  EUR: 0.335,
  GBP: 0.390,
  INR: 0.00365,
  AED: 0.0836,
  SAR: 0.0819,
  BHD: 0.815,
  OMR: 0.798,
  QAR: 0.0843,
};

// Canonical currency codes for display
export const CURRENCY_DISPLAY: Record<string, string> = {
  KWD: "KWD",
  KD: "KWD",
  USD: "USD",
  EUR: "EUR",
  GBP: "GBP",
  INR: "INR",
  AED: "AED",
  SAR: "SAR",
  BHD: "BHD",
  OMR: "OMR",
  QAR: "QAR",
};

// Symbol map for display
export const CURRENCY_SYMBOLS: Record<string, string> = {
  KWD: "KWD",
  KD: "KWD",
  USD: "$",
  EUR: "\u20AC",
  GBP: "\u00A3",
  INR: "\u20B9",
  AED: "AED",
  SAR: "SAR",
  BHD: "BHD",
  OMR: "OMR",
  QAR: "QAR",
};

/** Normalizes display codes and symbols without depending on server-side FX persistence. */
export function normalizeCurrencyCode(currency: string): string {
  const raw = String(currency ?? "").trim().toUpperCase();
  const symbolMap: Record<string, string> = { "$": "USD", "€": "EUR", "£": "GBP", "₹": "INR", "RS": "INR", "KD": "KWD" };
  const compact = raw.replace(/[^A-Z]/g, "");
  return symbolMap[raw] ?? symbolMap[compact] ?? (compact || "KWD");
}

export function convertToKwd(amount: number, currency: string): number {
  const code = normalizeCurrencyCode(currency);
  const rate = CURRENCY_TO_KWD[code] ?? 1;
  return Math.round(amount * rate * 1000) / 1000;
}

export function getCurrencyDisplay(currency: string): string {
  const normalized = currency.toUpperCase().replace(/[^A-Z]/g, "");
  const symbolMap: Record<string, string> = { "$": "USD", "RS": "INR" };
  const code = symbolMap[normalized] ?? normalized;
  return CURRENCY_DISPLAY[code] ?? code ?? "KWD";
}

export function formatAmount(amount: number | string, currency?: string | null): string {
  const num = typeof amount === "string" ? parseFloat(amount) : amount;
  const cur = getCurrencyDisplay(currency ?? "KWD");
  // Use thousands separators for readability (e.g., 1,234,567.890)
  const formatted = num.toLocaleString("en-US", {
    minimumFractionDigits: 3,
    maximumFractionDigits: 3,
  });
  return `${cur} ${formatted}`;
}

export type GroupBy = "merchant" | "category" | "daily" | "weekly" | "monthly";

export interface TransactionGroup {
  key: string;
  label: string;
  count: number;
  totalAmount: number;
  type: "cash_in" | "cash_out" | "mixed";
}
